import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CheckinDisplayState } from 'src/app/models/checkin';
import { CheckinService } from 'src/app/services/checkin/checkin.service';

export const DISPLAY_TOKEN_KEY = 'li-checkin-display-token';

const WEEKDAYS = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/**
 * Full-screen check-in display of one plan (room), e.g. on a tablet:
 * /checkin/<pid>. Shows the rotating QR code of the course that is open
 * for check-in. Set up once with the link from the back office page
 * "Anzeigen" (/checkin/<pid>#setup=<token>); the token stays in this
 * browser.
 */
@Component({
  selector: 'li-checkin-display',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './checkin-display.component.html',
  styleUrls: ['./checkin-display.component.scss']
})
export class CheckinDisplayComponent implements OnInit, OnDestroy {
  public state?: CheckinDisplayState;
  public status: 'loading' | 'ok' | 'not-set-up' | 'offline' = 'loading';
  public clock = '';

  private pid = 0;
  private token: string | null = null;
  private refreshTimer?: ReturnType<typeof setTimeout>;
  private clockTimer?: ReturnType<typeof setInterval>;
  private request?: Subscription;
  private wakeLock?: { release(): Promise<void> };

  constructor(
    private checkinService: CheckinService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.pid = Number(this.route.snapshot.paramMap.get('pid')) || 0;

    const setup = /(?:^|&)setup=([A-Za-z0-9_-]{43})/.exec(this.route.snapshot.fragment ?? '');
    if (setup) {
      this.store(setup[1]);
      // Don't leave the token in the address bar or history.
      this.router.navigate([], { relativeTo: this.route, replaceUrl: true, fragment: undefined });
    }
    this.token = this.load();

    this.tick();
    this.clockTimer = setInterval(() => this.tick(), 1000);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.keepScreenOn();
    this.refresh();
  }

  ngOnDestroy(): void {
    clearTimeout(this.refreshTimer);
    clearInterval(this.clockTimer);
    this.request?.unsubscribe();
    document.removeEventListener('visibilitychange', this.onVisibility);
    this.wakeLock?.release().catch(() => undefined);
  }

  /** "Nächster Kurs: Di 03.11., 17:00 · Name", without the date if it is today. */
  public get nextText(): string {
    const next = this.state?.next;
    if (!next) return '';
    const today = this.state!.now.slice(0, 10);
    const [y, m, d] = next.date.split('-').map(Number);
    const day = next.date === today
      ? ''
      : `${WEEKDAYS[new Date(y, m - 1, d).getDay()]} ${String(d).padStart(2, '0')}.${String(m).padStart(2, '0')}., `;
    return `Nächster Kurs: ${day}${next.begin} · ${next.name} ${next.description}`.trim();
  }

  private refresh(): void {
    clearTimeout(this.refreshTimer);
    if (!this.token || this.pid <= 0) {
      this.status = 'not-set-up';
      return;
    }

    this.request?.unsubscribe();
    this.request = this.checkinService.displayState(this.pid, this.token).subscribe({
      next: state => {
        this.state = state;
        this.status = 'ok';
        // Shortly after the codes change, so the new one is already valid.
        this.schedule(state.secondsLeft * 1000 + 500);
      },
      error: (error: HttpErrorResponse) => {
        if (error.status === 401) {
          this.status = 'not-set-up';
          return;
        }
        this.status = 'offline';
        this.schedule(10000);
      }
    });
  }

  private schedule(ms: number): void {
    clearTimeout(this.refreshTimer);
    this.refreshTimer = setTimeout(() => this.refresh(), ms);
  }

  private tick(): void {
    const now = new Date();
    this.clock = `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
  }

  // Back in the foreground (tablet woke up): fetch at once, the code may be old.
  private onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      this.keepScreenOn();
      if (this.status !== 'not-set-up') this.refresh();
    }
  };

  private async keepScreenOn(): Promise<void> {
    try {
      const wakeLock = (navigator as Navigator & { wakeLock?: { request(type: 'screen'): Promise<{ release(): Promise<void> }> } }).wakeLock;
      this.wakeLock = await wakeLock?.request('screen');
    } catch {
      // Not supported or not allowed; the tablet's own setting has to do.
    }
  }

  private store(token: string): void {
    try { localStorage.setItem(DISPLAY_TOKEN_KEY, token); } catch { /* storage blocked */ }
  }

  private load(): string | null {
    try { return localStorage.getItem(DISPLAY_TOKEN_KEY); } catch { return null; }
  }
}
